import React from "react";
import { IonCard, IonCardContent, IonCardHeader, IonCol, IonRow } from "@ionic/react";
import SkeletonBlock from "./SkeletonBlock";
import {
    SUBSECTION_LEADING_ICON_BORDER_RADIUS_PX,
    SUBSECTION_LEADING_ICON_BOX_PX,
} from "../Entry/EntryForm/SubSectionCard";
import { ENTRY_FORM_SECTION_CONTENT_GAP_PX } from "../Entry/EntryForm/EntryFormSectionHeader";
import {
    BARN_CARD_CONTENT_MIN_HEIGHT_PX,
    CLASS_CARD_BLOCK_PX,
    CLASS_CARD_SEARCH_PX,
    CLASS_CARD_TITLE_PX,
    EVENT_INFO_CONTACT_ROW_PX,
    EVENT_INFO_SUBTITLE_LINE_PX,
    EVENT_INFO_TITLE_LINE_PX,
    FORM_FIELD_INPUT_PX,
    FORM_FIELD_LABEL_GAP_PX,
    FORM_FIELD_LABEL_PX,
    PARENT_CARD_CHIP_HEIGHT_PX,
    PARENT_CARD_CHIP_WIDTH_EVENT_INFO_PX,
    PARENT_CARD_CHIP_WIDTH_NOT_STARTED_PX,
    PARENT_CARD_CTA_HEIGHT_PX,
    PARENT_CARD_LEADING_ICON_BOX_PX,
    PARENT_CARD_LEADING_ICON_RADIUS_PX,
    PARENT_CARD_TRASH_ICON_PX,
    REACT_SELECT_CONTROL_HEIGHT_PX,
    SKELETON_DISPLAY_3_LINE_PX,
    SKELETON_DISPLAY_4_LINE_PX,
    SKELETON_TITLE_SUBTITLE_GAP_PX,
    STABLING_NOTES_CARD_MIN_HEIGHT_PX,
    SUMMARY_COLLAPSED_PREVIEW_ICON_PX,
    SUMMARY_COLLAPSED_PREVIEW_ICON_RADIUS_PX,
    SUMMARY_COLLAPSED_PREVIEW_MIN_HEIGHT_PX,
} from "./skeletonSizing";

const ParentCardChipSkeleton: React.FC<{ width: number }> = ({ width }) => (
    <SkeletonBlock height={PARENT_CARD_CHIP_HEIGHT_PX} width={width} borderRadius={16} />
);

/** Collapsed parent cards — `sizeMd="8"` VIEW / Start / Edit; full width in compact entry grid. */
const ParentCardCtaSkeleton: React.FC<{ fillAvailableHeight?: boolean }> = ({ fillAvailableHeight = false }) => (
    <IonRow className="ion-justify-content-center">
        <IonCol sizeXs="12" sizeMd={fillAvailableHeight ? "12" : "8"} className="ion-text-center py-0 px-2">
            <SkeletonBlock height={PARENT_CARD_CTA_HEIGHT_PX} borderRadius={8} />
        </IonCol>
    </IonRow>
);

/** Expanded Event Info — `sizeMd="4"` CLOSE button. */
const ParentCardCloseCtaSkeleton: React.FC = () => (
    <IonRow className="ion-justify-content-center">
        <IonCol sizeXs="11" sizeMd="4" className="p-0">
            <SkeletonBlock height={PARENT_CARD_CTA_HEIGHT_PX} borderRadius={8} />
        </IonCol>
    </IonRow>
);

const SummaryCollapsedPreviewSkeleton: React.FC = () => (
    <IonRow className="mx-0">
        <IonCol size="12" className="py-0 px-2">
            <div className="mb-2">
                <div
                    style={{
                        borderRadius: 12,
                        backgroundColor: "#ffffff",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                        minHeight: SUMMARY_COLLAPSED_PREVIEW_MIN_HEIGHT_PX,
                        padding: "8px 10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                    }}>
                    <SkeletonBlock
                        height={SUMMARY_COLLAPSED_PREVIEW_ICON_PX}
                        width={SUMMARY_COLLAPSED_PREVIEW_ICON_PX}
                        borderRadius={SUMMARY_COLLAPSED_PREVIEW_ICON_RADIUS_PX}
                        style={{ flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <SkeletonBlock height={14} width="42%" style={{ marginBottom: 4 }} />
                        <SkeletonBlock height={18} width="68%" />
                    </div>
                </div>
            </div>
        </IonCol>
    </IonRow>
);

export type EntrySectionCardSkeletonVariant = "eventInfo" | "entry" | "incompleteEntry" | "section" | "summary";

/** Collapsed `EntrySectionCard` placeholder — mirrors header, chip, and CTA layout. */
export const EntrySectionCardSkeleton: React.FC<{
    variant?: EntrySectionCardSkeletonVariant;
    showTrash?: boolean;
    /** Event Info defaults expanded on New Entry page load (`expandedCard === "header"`). */
    isExpanded?: boolean;
    /** Stretch to fill a compact multi-entry grid cell, matching `EntrySectionCard`. */
    fillAvailableHeight?: boolean;
    /** Optional min height for the card body in compact grid (from measured sibling cards). */
    minCardHeight?: number;
}> = ({ variant = "section", showTrash = false, isExpanded = false, fillAvailableHeight = false, minCardHeight }) => {
    const isEventInfoExpanded = variant === "eventInfo" && isExpanded;
    const chipWidth =
        variant === "eventInfo" ? PARENT_CARD_CHIP_WIDTH_EVENT_INFO_PX : PARENT_CARD_CHIP_WIDTH_NOT_STARTED_PX;

    const textBlockMinHeight =
        variant === "entry"
            ? SKELETON_DISPLAY_4_LINE_PX + 6 + SKELETON_DISPLAY_3_LINE_PX
            : variant === "incompleteEntry"
              ? SKELETON_DISPLAY_3_LINE_PX + SKELETON_TITLE_SUBTITLE_GAP_PX + SKELETON_DISPLAY_4_LINE_PX
              : variant === "eventInfo"
                ? SKELETON_DISPLAY_4_LINE_PX + 8 + SKELETON_DISPLAY_3_LINE_PX
                : SKELETON_DISPLAY_3_LINE_PX + SKELETON_TITLE_SUBTITLE_GAP_PX + SKELETON_DISPLAY_4_LINE_PX;

    return (
        <IonRow className="p-0" style={fillAvailableHeight ? { height: "100%", minWidth: 0, maxWidth: "100%" } : undefined}>
            <IonCol
                size="12"
                className="p-0 m-0"
                style={
                    fillAvailableHeight
                        ? { height: "100%", display: "flex", flexDirection: "column", minWidth: 0, maxWidth: "100%" }
                        : undefined
                }>
                <IonCard
                    mode="md"
                    className={`rounded-3 bg-light ${fillAvailableHeight ? "mx-1 m-0" : "mt-2 mb-0 mx-1"}`}
                    style={{
                        ...(fillAvailableHeight
                            ? {
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  minWidth: 0,
                                  maxWidth: "100%",
                                  boxSizing: "border-box",
                                  height: "100%",
                                  overflow: "hidden",
                              }
                            : null),
                        ...(minCardHeight ? { minHeight: Math.max(0, minCardHeight - 8) } : null),
                    }}>
                    <IonCardHeader className="px-2 pt-2 pb-0">
                        <IonRow className="ion-align-items-start ion-justify-content-between">
                            <IonCol className="pt-0" style={{ minWidth: 0 }}>
                                <div className="d-flex" style={{ gap: "8px", alignItems: "flex-start" }}>
                                    <SkeletonBlock
                                        height={PARENT_CARD_LEADING_ICON_BOX_PX}
                                        width={PARENT_CARD_LEADING_ICON_BOX_PX}
                                        borderRadius={PARENT_CARD_LEADING_ICON_RADIUS_PX}
                                        style={{ flexShrink: 0 }}
                                    />
                                    <div style={{ flex: 1, minWidth: 0, minHeight: Math.max(PARENT_CARD_LEADING_ICON_BOX_PX, textBlockMinHeight) }}>
                                        {variant === "eventInfo" && (
                                            <>
                                                <SkeletonBlock height={SKELETON_DISPLAY_4_LINE_PX} width="52%" style={{ marginBottom: 8 }} />
                                                <SkeletonBlock height={SKELETON_DISPLAY_3_LINE_PX} width="78%" />
                                            </>
                                        )}
                                        {variant === "entry" && (
                                            <>
                                                <SkeletonBlock height={SKELETON_DISPLAY_4_LINE_PX} width="28%" style={{ marginBottom: 6 }} />
                                                <SkeletonBlock height={SKELETON_DISPLAY_3_LINE_PX} width="62%" />
                                            </>
                                        )}
                                        {variant === "incompleteEntry" && (
                                            <>
                                                <SkeletonBlock
                                                    height={SKELETON_DISPLAY_3_LINE_PX}
                                                    width="62%"
                                                    style={{ marginBottom: SKELETON_TITLE_SUBTITLE_GAP_PX }}
                                                />
                                                <SkeletonBlock height={SKELETON_DISPLAY_4_LINE_PX} width="48%" />
                                            </>
                                        )}
                                        {(variant === "section" || variant === "summary") && (
                                            <>
                                                <SkeletonBlock height={SKELETON_DISPLAY_3_LINE_PX} width="56%" style={{ marginBottom: SKELETON_TITLE_SUBTITLE_GAP_PX }} />
                                                <SkeletonBlock height={SKELETON_DISPLAY_4_LINE_PX} width="72%" />
                                            </>
                                        )}
                                    </div>
                                </div>
                            </IonCol>
                            {showTrash ? (
                                <IonCol size="auto" className="pt-0 d-flex ion-align-items-start">
                                    <SkeletonBlock height={PARENT_CARD_TRASH_ICON_PX} width={PARENT_CARD_TRASH_ICON_PX} borderRadius={6} />
                                </IonCol>
                            ) : null}
                        </IonRow>
                        <IonRow
                            className="ion-align-items-center ion-justify-content-between pb-2 pt-2"
                            style={{ paddingBottom: ENTRY_FORM_SECTION_CONTENT_GAP_PX }}>
                            <IonCol size="auto" className="pt-0">
                                <ParentCardChipSkeleton width={chipWidth} />
                            </IonCol>
                        </IonRow>
                    </IonCardHeader>
                    <IonCardContent
                        className="px-0 pt-0 pb-2"
                        style={
                            fillAvailableHeight
                                ? { flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end" }
                                : undefined
                        }>
                        {isEventInfoExpanded ? (
                            <IonRow className="ion-justify-content-center">
                                <IonCol sizeXs="12" sizeMd="12" className="px-2 py-0 mx-3">
                                    <EventInfoCardSkeleton />
                                    <ParentCardCloseCtaSkeleton />
                                </IonCol>
                            </IonRow>
                        ) : (
                            <>
                                {variant === "summary" && <SummaryCollapsedPreviewSkeleton />}
                                <ParentCardCtaSkeleton fillAvailableHeight={fillAvailableHeight} />
                            </>
                        )}
                    </IonCardContent>
                </IonCard>
            </IonCol>
        </IonRow>
    );
};

/** Add / Copy actions — adjacent, sharing the full row width. */
export const AddAnotherEntryButtonSkeleton: React.FC = () => (
    <IonRow className="ion-justify-content-center" style={{ boxSizing: "border-box", paddingLeft: 6, paddingRight: 6 }}>
        <IonCol size="12" className="ion-text-center p-0">
            <div className="entry-add-actions-row">
                <div className="entry-add-action-col">
                    <SkeletonBlock height={PARENT_CARD_CTA_HEIGHT_PX} borderRadius={8} />
                </div>
                <div className="entry-add-action-col">
                    <SkeletonBlock height={PARENT_CARD_CTA_HEIGHT_PX} borderRadius={8} />
                </div>
            </div>
        </IonCol>
    </IonRow>
);

/** Full New Entry Redesign page while event + entries load. */
export const NewEntryPageSkeleton: React.FC = () => (
    <IonCol className="p-0 m-0" style={{ paddingBottom: 8 }}>
        <EntrySectionCardSkeleton variant="eventInfo" isExpanded />
        <AddAnotherEntryButtonSkeleton />
        <EntrySectionCardSkeleton variant="section" />
        <EntrySectionCardSkeleton variant="section" />
        <EntrySectionCardSkeleton variant="summary" />
    </IonCol>
);

/** Inline form fields while a subsection loads progressively. */
export const FormFieldsInlineSkeleton: React.FC<{ fieldCount?: number }> = ({ fieldCount = 2 }) => (
    <IonCol sizeXs="12" sizeMd="6" className="ion-align-self-start pt-2">
        <IonRow className="p-0 m-0">
            {Array.from({ length: fieldCount }, (_, i) => (
                <IonCol key={i} sizeXs="12" sizeMd={fieldCount === 1 ? "12" : "6"} className="pb-2">
                    <SkeletonBlock height={FORM_FIELD_LABEL_PX} width="38%" style={{ marginBottom: FORM_FIELD_LABEL_GAP_PX }} />
                    <SkeletonBlock height={FORM_FIELD_INPUT_PX} borderRadius={6} />
                </IonCol>
            ))}
        </IonRow>
    </IonCol>
);

/** React-select control (`SelectBarnFromAllBarns`). */
export const SearchSelectSkeleton: React.FC = () => <SkeletonBlock height={REACT_SELECT_CONTROL_HEIGHT_PX} borderRadius={4} />;

/** Barn picker radio cards. */
export const BarnCardsSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => (
    <IonRow className="p-0 m-0 ion-align-items-stretch" style={{ rowGap: ENTRY_FORM_SECTION_CONTENT_GAP_PX }}>
        {Array.from({ length: count }, (_, i) => (
            <IonCol key={i} sizeXs="12" sizeSm="4" className="py-0 px-1 d-flex">
                <IonCard
                    className="rounded-3 p-0 m-0 bg-white w-100"
                    style={{
                        minHeight: BARN_CARD_CONTENT_MIN_HEIGHT_PX,
                        height: "100%",
                        display: "flex",
                    }}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "stretch",
                            width: "100%",
                            minHeight: BARN_CARD_CONTENT_MIN_HEIGHT_PX,
                            flex: 1,
                        }}>
                        <div
                            style={{
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "center",
                                padding: "14px 6px 12px 10px",
                            }}>
                            <SkeletonBlock height={22} width={22} borderRadius="50%" />
                        </div>
                        <div
                            style={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                flexDirection: "column",
                                padding: "10px 12px 10px 4px",
                            }}>
                            <div className="pb-1">
                                <SkeletonBlock height={SKELETON_DISPLAY_4_LINE_PX} width="58%" />
                            </div>
                            <div className="flex-grow-1 d-flex flex-column">
                                <SkeletonBlock height={14} style={{ marginBottom: 6 }} />
                                <SkeletonBlock height={14} width="88%" style={{ marginBottom: 6 }} />
                                <SkeletonBlock height={14} width="72%" style={{ marginBottom: 10 }} />
                                <div
                                    className="d-flex ion-justify-content-between ion-align-items-center"
                                    style={{ marginTop: "auto", paddingTop: "8px" }}>
                                    <SkeletonBlock height={PARENT_CARD_CHIP_HEIGHT_PX - 4} width={120} borderRadius={16} />
                                    <SkeletonBlock height={20} width={20} borderRadius={4} />
                                </div>
                            </div>
                        </div>
                    </div>
                </IonCard>
            </IonCol>
        ))}
    </IonRow>
);

/** Coggins file-upload row. */
export const CogginsFileUploadSkeleton: React.FC = () => (
    <IonRow className="p-0 m-0">
        <IonCol size="12" className="pt-0">
            <SkeletonBlock height={FORM_FIELD_LABEL_PX} width="42%" style={{ marginBottom: FORM_FIELD_LABEL_GAP_PX }} />
            <SkeletonBlock height={88} borderRadius={12} />
        </IonCol>
    </IonRow>
);

/** `EventInfoSection` expanded white card. */
export const EventInfoCardSkeleton: React.FC = () => (
    <IonCard className="rounded-3 bg-white p-0 m-0 mb-2">
        <IonCardHeader className="pb-0 px-3 pt-3">
            <SkeletonBlock height={EVENT_INFO_SUBTITLE_LINE_PX} width="48%" style={{ marginBottom: 10 }} />
            <SkeletonBlock height={EVENT_INFO_TITLE_LINE_PX} width="74%" />
        </IonCardHeader>
        <IonCardContent className="p-2 pb-3">
            {Array.from({ length: 3 }, (_, i) => (
                <IonRow key={i} className="ion-align-items-center p-0" style={{ minHeight: EVENT_INFO_CONTACT_ROW_PX, marginBottom: i < 2 ? 4 : 0 }}>
                    <IonCol sizeXs="2" sizeMd="1" className="p-0 ion-text-center">
                        <SkeletonBlock height={20} width={20} borderRadius={6} />
                    </IonCol>
                    <IonCol sizeXs="10" sizeMd="11" className="p-0">
                        <SkeletonBlock height={16} width={i === 0 ? "92%" : "64%"} />
                    </IonCol>
                </IonRow>
            ))}
        </IonCardContent>
    </IonCard>
);

/** `ClassInfoSection` — Class List + Selected Classes cards. */
export const ClassInfoCardsSkeleton: React.FC = () => (
    <>
        {["Class List", "Selected Classes"].map((title) => (
            <IonCard
                key={title}
                className="rounded-3 p-0 mx-0 bg-white"
                style={{ marginTop: 0, marginBottom: ENTRY_FORM_SECTION_CONTENT_GAP_PX }}>
                <IonCardHeader className="px-2 py-2" style={{ minHeight: CLASS_CARD_TITLE_PX + 16 }}>
                    <SkeletonBlock height={CLASS_CARD_TITLE_PX} width="38%" />
                </IonCardHeader>
                <IonCardContent className="px-2 pb-3">
                    <SkeletonBlock height={CLASS_CARD_SEARCH_PX} borderRadius={8} style={{ marginBottom: 12 }} />
                    <SkeletonBlock height={CLASS_CARD_BLOCK_PX} borderRadius={8} style={{ marginBottom: 8 }} />
                    <SkeletonBlock height={CLASS_CARD_BLOCK_PX} borderRadius={8} />
                </IonCardContent>
            </IonCard>
        ))}
    </>
);

/** `SubSectionCard` blocks for waivers / stabling load & save. */
export const SubSectionCardsSkeleton: React.FC<{ count?: number; includeStablingNotes?: boolean }> = ({
    count = 2,
    includeStablingNotes = true,
}) => (
    <>
        {Array.from({ length: count }, (_, i) => (
            <div key={i} className="mb-2">
                <IonCard className="mx-0 my-1" style={{ borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)" }}>
                    <div className="px-2 py-2" style={{ backgroundColor: "rgba(119, 136, 153, 0.35)", minHeight: SUBSECTION_LEADING_ICON_BOX_PX + 16 }}>
                        <div className="d-flex" style={{ gap: 8, alignItems: "center" }}>
                            <SkeletonBlock
                                height={SUBSECTION_LEADING_ICON_BOX_PX}
                                width={SUBSECTION_LEADING_ICON_BOX_PX}
                                borderRadius={SUBSECTION_LEADING_ICON_BORDER_RADIUS_PX}
                                style={{ flexShrink: 0 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <SkeletonBlock height={14} width="30%" style={{ marginBottom: 2 }} />
                                <SkeletonBlock height={20} width="70%" style={{ marginBottom: 2 }} />
                                <SkeletonBlock height={14} width="50%" />
                            </div>
                        </div>
                    </div>
                    <IonCardContent className="px-2 py-2">
                        <SkeletonBlock height={REACT_SELECT_CONTROL_HEIGHT_PX} borderRadius={8} style={{ marginBottom: 8 }} />
                        <SkeletonBlock height={52} borderRadius={8} style={{ marginBottom: 8 }} />
                        <SkeletonBlock height={52} borderRadius={8} />
                    </IonCardContent>
                </IonCard>
            </div>
        ))}
        {includeStablingNotes && (
            <IonCard className="mx-0 my-0 mt-2" style={{ borderRadius: 12, overflow: "hidden", minHeight: STABLING_NOTES_CARD_MIN_HEIGHT_PX }}>
                <IonCardContent className="px-3 py-2">
                    <SkeletonBlock height={18} width="38%" style={{ marginBottom: 8 }} />
                    <SkeletonBlock height={96} borderRadius={8} />
                </IonCardContent>
            </IonCard>
        )}
    </>
);
